import { FC } from "react";
import { ColumnVisibilityState } from "@tanstack/react-table";
import { Accordion } from "@app/shared/Accordion";
import { SortByOption, ViewMode } from "@app/network-portfolio-management/interfaces";
import { ProjectInfiniteList } from "@app/network-portfolio-management/components/ProjectInfiniteList";
import { ProjectTable } from "@app/network-portfolio-management/components/project-table/ProjectTable";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { convertTextToKebabCase } from "@app/shared/utility/convertTextToKebabCase";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";

interface GroupSectionProps {
  columnVisibility: ColumnVisibilityState;
  groupName: string;
  /** Total projects in this group (from the backend partition), shown in the header and known upfront. */
  count: number;
  projectIds: number[];
  sortBy: SortByOption;
  /**
   * When true, `projectIds` are pre-sorted by the backend and will be sliced client-side per page.
   * Should always be true for grouped results since the grouped endpoint returns pre-sorted IDs.
   */
  isSorted?: boolean;
  onColumnVisibilityChange: (visibility: ColumnVisibilityState) => void;
  onToggleSelect: (id: number) => void;
  /** Selects or clears this group's entire partition; scoped so other groups are never affected. */
  onToggleGroup: (groupProjectIds: number[], checked: boolean) => void;
  /** True when every project in this group is selected — drives the header checkbox's checked state. */
  groupAllSelected: boolean;
  /** True when some but not all of this group's projects are selected — drives the indeterminate state. */
  groupSomeSelected: boolean;
  selectedProjectIds: Set<number>;
  /** When false the per-project selection checkbox is hidden (no Project Action filter active). */
  selectionEnabled: boolean;
  viewMode: ViewMode;
}

/**
 * Collapsible section for a single group partition. All sections render collapsed;
 * the count comes from the backend partition so it is exact and known immediately. On first expand the
 * projects are fetched lazily (sorted on the backend) and the group scrolls with infinite scroll —
 * the user collapses a group to scroll past it, so the sentinel never sits in the page scroll path
 * while collapsed.
 */
export const GroupSection: FC<GroupSectionProps> = ({
  columnVisibility,
  groupName,
  count,
  projectIds,
  sortBy,
  isSorted,
  onColumnVisibilityChange,
  onToggleSelect,
  onToggleGroup,
  groupAllSelected,
  groupSomeSelected,
  selectedProjectIds,
  selectionEnabled,
  viewMode,
}) => {
  const groupSlug = convertTextToKebabCase(groupName);

  const selectAllAction = selectionEnabled ? (
    <RoleRestricted allowedRoles={[Roles.QUEST_USER, Roles.CLIENT_ADMIN]}>
      <div className="network-portfolio-management-lob-group-section__select-all">
        <QESCheckbox
          aria-label={`Select all projects in ${groupName}`}
          checked={groupAllSelected}
          indeterminate={groupSomeSelected && !groupAllSelected}
          label="Select All"
          testId={`network-portfolio-management-group-select-all-${groupSlug}`}
          onChange={(e) => onToggleGroup(projectIds, e.target.checked)}
        />
      </div>
    </RoleRestricted>
  ) : undefined;

  return (
    <div className="network-portfolio-management-lob-group-section">
      <Accordion
        testId={`network-portfolio-management-lob-group-${groupSlug}`}
        title={`${groupName} (${count})`}
        variant="flat"
        defaultOpen={false}
        lazyLoad={true}
        headerLeading={selectAllAction}
      >
        {viewMode === "rows" ? (
          <ProjectTable
            columnVisibility={columnVisibility}
            projectIds={projectIds}
            sortBy={sortBy}
            selectedProjectIds={selectedProjectIds}
            onColumnVisibilityChange={onColumnVisibilityChange}
            onToggleSelect={onToggleSelect}
            selectionEnabled={selectionEnabled}
            isSorted={isSorted}
            testIdPrefix={`network-portfolio-management-project-table-${groupSlug}`}
          />
        ) : (
          <ProjectInfiniteList
            projectIds={projectIds}
            sortBy={sortBy}
            selectedProjectIds={selectedProjectIds}
            onToggleSelect={onToggleSelect}
            selectionEnabled={selectionEnabled}
            isSorted={isSorted}
          />
        )}
      </Accordion>
    </div>
  );
};
